// Pure helpers for the Feed-health panel. The whole point of this view is to
// distinguish three states that look identical in the run list (which only
// shows packages that resulted from a feed):
//
//   - ok      : the feed was read & parsed (parsed > 0). newItems may be 0 —
//               that's the COMMON, healthy case ("nothing new since last run").
//   - broken  : a fetch happened but parsed 0 entries — the feed URL changed,
//               the parser broke, or the feed went empty. THIS is the alarm.
//   - silent  : no fetch records at all for the day — either the feed/ prefix
//               isn't being written (IAM grant / deploy) or the workflow didn't
//               run. Can't confirm health either way.

import type { BundleIndex, FeedSummary } from "./types";

export type FeedState = "ok" | "broken" | "silent";

export type FeedHealth = {
  state: FeedState;
  tone: "good" | "bad" | "muted";
  // One-line headline for the feed row.
  headline: string;
  // New posts seen across the whole window for this feed (feed.totalNewItems).
  // This is the headline "how many new" number — NOT latest.newItems, which is
  // usually 0 because earlier fetches in the window already marked posts seen.
  newPosts: number;
  // The parsed → in-category pipeline for the latest fetch. null when silent
  // (no fetch to describe).
  parsed: number | null;
  inCategory: number | null;
  // The narrowest filter the feed actually applies, ready to render as
  // "N parsed · M <label>". null when the feed filters on nothing, in which
  // case there is no funnel to show. See narrowestFilterStage.
  filterStage: { label: string; count: number } | null;
};

// Which filter to show in the one-line pipeline. A feed can carry a category
// filter, a keyword filter, an age cutoff, or none, and only the LAST one that
// runs tells you how many posts actually reached the classifier. The earlier
// counts are intermediate. Ordered to match fetchFeed: category → keyword →
// age. Returns null for an unfiltered feed, and for any pre-filter record
// whose counts are absent.
export function narrowestFilterStage(
  feed: FeedSummary,
): { label: string; count: number } | null {
  const latest = feed.latest;
  if (!latest) return null;
  if (feed.maxAgeDays != null && latest.afterAgeFilter != null) {
    return { label: `within ${feed.maxAgeDays}d`, count: latest.afterAgeFilter };
  }
  if ((feed.keywordFilter?.length ?? 0) > 0 && latest.afterKeywordFilter != null) {
    return { label: "keyword match", count: latest.afterKeywordFilter };
  }
  if ((feed.categoryFilter?.length ?? 0) > 0) {
    return { label: "in-category", count: latest.afterCategoryFilter };
  }
  return null;
}

export function feedHealth(feed: FeedSummary): FeedHealth {
  const latest = feed.latest;
  if (!latest || feed.fetchCount === 0) {
    return {
      state: "silent",
      tone: "muted",
      headline: "No fetches recorded — can't confirm health",
      newPosts: 0,
      parsed: null,
      inCategory: null,
      filterStage: null,
    };
  }
  if (latest.parsed === 0) {
    return {
      state: "broken",
      tone: "bad",
      headline: "Parsed 0 entries — feed or parser may be broken",
      newPosts: feed.totalNewItems,
      parsed: 0,
      inCategory: 0,
      filterStage: null,
    };
  }
  return {
    state: "ok",
    tone: "good",
    headline:
      feed.totalNewItems > 0
        ? `${feed.totalNewItems} new post${feed.totalNewItems === 1 ? "" : "s"}`
        : "Read & parsed — nothing new",
    newPosts: feed.totalNewItems,
    parsed: latest.parsed,
    inCategory: latest.afterCategoryFilter,
    filterStage: narrowestFilterStage(feed),
  };
}

// Short, human label for a feed (sourceId is the stable key; this is for display).
export function feedLabel(feed: FeedSummary): string {
  switch (feed.sourceId) {
    case "socket-blog":
      return "Socket";
    case "stepsecurity-blog":
      return "StepSecurity";
    case "nextjs-blog":
      return "Next.js";
    default:
      return feed.sourceId;
  }
}

// Overall RSS banner state for the day across all feeds: bad if any feed is
// broken, muted if there are no feeds at all, else good.
export function overallFeedTone(feeds: FeedSummary[]): "good" | "bad" | "muted" {
  if (feeds.length === 0) return "muted";
  if (feeds.some((f) => feedHealth(f).state === "broken")) return "bad";
  if (feeds.every((f) => feedHealth(f).state === "silent")) return "muted";
  return "good";
}

// OUR classification of a blog post, decided by the Anthropic classify step
// and read back from its log events — NOT the feed's own RSS categories.
export type Classification = {
  category: string; // one of compromise_alert | research | marketing | news_summary | other
  rationale: string | null;
};

// Normalize a post URL used to join feed items to classification logs. A feed
// can vary trivial formatting between fetches — a trailing slash, http↔https,
// or host casing — and an exact string match would then drop an already-
// classified post back to "—". We key on a scheme-less host+path (+query) so
// those variants collide. Path case is preserved (paths can be significant).
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  try {
    const parsed = new URL(trimmed);
    const host = parsed.host.toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, ""); // drop trailing slash(es)
    return `${host}${path}${parsed.search}`;
  } catch {
    return trimmed.replace(/\/+$/, "").toLowerCase();
  }
}

// Map each post URL to what WE classified it as, aggregated across every run in
// the bundle. Only posts that were NEW in this window got classified, so a URL
// absent from the map simply wasn't classified in-window (seen/older post).
// Keyed on a normalized form of the classify log's data.sourceUrl (the feed
// item's link) so the UI join tolerates trivial URL-formatting drift.
export function classificationsByUrl(index: BundleIndex): Map<string, Classification> {
  const byUrl = new Map<string, Classification>();
  for (const run of index.runs) {
    for (const event of run.classifications ?? []) {
      const data = event.data ?? {};
      const url = typeof data.sourceUrl === "string" ? data.sourceUrl : null;
      const category = typeof data.category === "string" ? data.category : null;
      if (!url || !category) continue;
      const key = normalizeUrl(url);
      // First classification for a URL wins; repeats across runs are the same post.
      if (byUrl.has(key)) continue;
      byUrl.set(key, {
        category,
        rationale: typeof data.rationale === "string" ? data.rationale : null,
      });
    }
  }
  return byUrl;
}

// Map each processed post URL to the runId that processed it, i.e. the run
// whose Anthropic classify step logged that post's sourceUrl. Lets the UI jump
// from a blog entry to the run that handled it. Keyed on normalizeUrl (same as
// classificationsByUrl) so it tolerates trivial URL-formatting drift. First run
// wins if the same post somehow appears across runs.
export function runIdByPostUrl(index: BundleIndex): Map<string, string> {
  const byUrl = new Map<string, string>();
  for (const run of index.runs) {
    for (const event of run.classifications ?? []) {
      const url = typeof event.data?.sourceUrl === "string" ? event.data.sourceUrl : null;
      if (!url) continue;
      const key = normalizeUrl(url);
      if (!byUrl.has(key)) byUrl.set(key, run.runId);
    }
  }
  return byUrl;
}

// The two headline numbers for the top of the Feeds panel plus the packages
// context, scoped to the bundle's window. Everything here is already in the
// bundle — this just rolls it up so the UI doesn't recompute inline.
export type WindowSummary = {
  // Σ feed.totalNewItems — new blog posts across the whole window.
  newPosts: number;
  // Every package evaluated in the window (summary.totalPackages).
  packagesProcessed: number;
  // Packages that need a human: block + review.
  needAction: number;
  actionBreakdown: { block: number; review: number };
  // Human window label ("Last 24h" / "Last 7 days" / the UTC date).
  windowLabel: string;
  // Full start→end range for a tooltip, or null in date-mode bundles.
  windowRange: string | null;
};

export function windowSummary(index: BundleIndex): WindowSummary {
  const feeds = index.feeds ?? [];
  const newPosts = feeds.reduce((sum, feed) => sum + (feed.totalNewItems || 0), 0);
  const counts = index.summary.counts;
  const block = counts.block || 0;
  const review = counts.review || 0;
  return {
    newPosts,
    packagesProcessed: index.summary.totalPackages,
    needAction: block + review,
    actionBreakdown: { block, review },
    windowLabel: windowLabel(index),
    windowRange: windowRange(index),
  };
}

// "Last 24h" / "Last 7 days" / "Last Nd" / "Last Nh" for rolling bundles,
// derived from the window duration; falls back to the UTC date otherwise.
function windowLabel(index: BundleIndex): string {
  const hours = windowHours(index);
  if (hours === null) return index.date;
  if (Math.abs(hours - 24) < 1) return "Last 24h";
  if (Math.abs(hours - 168) < 6) return "Last 7 days";
  const days = Math.round(hours / 24);
  return days >= 2 ? `Last ${days} days` : `Last ${Math.round(hours)}h`;
}

function windowRange(index: BundleIndex): string | null {
  if (!index.windowStart || !index.windowEnd) return null;
  const fmt = (iso: string) => new Date(iso).toLocaleString();
  return `${fmt(index.windowStart)} → ${fmt(index.windowEnd)}`;
}

function windowHours(index: BundleIndex): number | null {
  if (!index.windowStart || !index.windowEnd) return null;
  const start = new Date(index.windowStart).getTime();
  const end = new Date(index.windowEnd).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  return (end - start) / 3_600_000;
}
